import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { toast } from "sonner";
import * as authLib from "@/lib/auth";
import { onAuthEvent } from "@/lib/auth-events";
import { fetchServerRole, type ServerRoleResult } from "@/lib/server-role";

interface CurrentUser {
  userId: string;
  email: string;
  name: string;
  role: "pm" | "sa" | "engineer" | "leadership" | "admin";
  groups: string[];
}

interface AuthContextType {
  user: CurrentUser | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  /**
   * True once `user.role` has been confirmed by the server (GET /api/users/manage?self=true).
   * False while unverified — the role is then least-privileged `engineer`, never the token role.
   */
  roleVerified: boolean;
  /**
   * Force a token refresh, then re-fetch the server-authoritative role. Resolves false when the
   * session can no longer be refreshed (e.g. revoked).
   */
  refreshUser: () => Promise<boolean>;
}

/** True when two users carry the same identity + authorization-relevant claims. */
function sameUser(a: CurrentUser | null, b: CurrentUser | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.userId === b.userId &&
    a.email === b.email &&
    a.name === b.name &&
    a.role === b.role &&
    a.groups.length === b.groups.length &&
    a.groups.every((g, i) => g === b.groups[i])
  );
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** How often a signed-in session re-checks its server-authoritative role. */
export const ROLE_RECHECK_INTERVAL_MS = 60_000;

const DEACTIVATED_MESSAGE =
  "Your account has been deactivated. Contact a Leadership user if you need access.";

/**
 * AuthProvider — identity from the Cognito ID token, ROLE FROM THE SERVER.
 *
 * The token's `cognito:groups` can be up to 1h stale (and survives a hard refresh via Cognito's
 * localStorage), so `user.role` is never taken from it. After login / restoreSession / any token
 * refresh, the role is fetched from GET /api/users/manage?self=true (DB-authoritative). Until that
 * first answer arrives `loading` stays true, so no role-gated UI renders with a token role. The role
 * is re-checked on window focus, every ROLE_RECHECK_INTERVAL_MS, and whenever the API reports a
 * role-denied 403. ACCOUNT_DEACTIVATED → logout + Cognito localStorage cleared. If the role cannot
 * be verified (network / 5xx) a never-verified session falls back to least-privileged `engineer`.
 */
export function AuthProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [roleVerified, setRoleVerified] = useState<boolean>(false);

  const userRef = useRef<CurrentUser | null>(null);
  userRef.current = user;
  const roleVerifiedRef = useRef(false);
  roleVerifiedRef.current = roleVerified;
  // Bumped on every login/logout so a slow role response for an old session is ignored.
  const sessionEpochRef = useRef(0);
  const inFlightRef = useRef<Promise<ServerRoleResult["status"]> | null>(null);

  const endSession = useCallback((reason?: "deactivated") => {
    sessionEpochRef.current += 1;
    authLib.logout();
    if (reason === "deactivated") {
      // restoreSession() would otherwise rebuild the session from the cached ID token.
      authLib.clearCognitoStorage();
      toast.error(DEACTIVATED_MESSAGE);
    }
    setUser(null);
    setRoleVerified(false);
  }, []);

  /** Replace the identity's token role with the server-authoritative role. */
  const applyServerRole = useCallback(
    async (identity: CurrentUser): Promise<ServerRoleResult["status"]> => {
      const epoch = sessionEpochRef.current;
      const result = await fetchServerRole();
      if (epoch !== sessionEpochRef.current) return result.status; // session changed meanwhile
      if (result.status === "deactivated") {
        endSession("deactivated");
        return result.status;
      }
      if (result.status === "ok") {
        const next: CurrentUser = { ...identity, role: result.caller.role };
        setUser((prev) => (sameUser(prev, next) ? prev : next));
        setRoleVerified(true);
        return result.status;
      }
      // Unverifiable: keep a role verified earlier for this same identity; otherwise least
      // privilege. Never fall back to the token role.
      setUser((prev) => {
        if (
          prev &&
          prev.userId === identity.userId &&
          roleVerifiedRef.current
        ) {
          return prev;
        }
        const next: CurrentUser = { ...identity, role: "engineer" };
        return sameUser(prev, next) ? prev : next;
      });
      return result.status;
    },
    [endSession],
  );

  /** Re-check the current session's server role (deduplicated while a check is in flight). */
  const recheckRole = useCallback(async (): Promise<void> => {
    const current = userRef.current;
    if (!current) return;
    if (!inFlightRef.current) {
      inFlightRef.current = applyServerRole(current).finally(() => {
        inFlightRef.current = null;
      });
    }
    await inFlightRef.current;
  }, [applyServerRole]);

  useEffect(() => {
    async function init(): Promise<void> {
      try {
        // 1. Identity from Cognito's localStorage session (survives page reload) — TOKEN role only.
        let identity = authLib.restoreSession();

        // 2. Access token expired or missing — try silent refresh
        // restoreSession() already loaded the refresh token into memory if available
        if (!identity) {
          const refreshed = await authLib.refreshTokens();
          if (refreshed) identity = authLib.getCurrentUser();
        }

        // 3. Role from the server before anything renders (loading stays true until then).
        if (identity) await applyServerRole(identity);
      } catch {
        // No valid session — user must log in
      } finally {
        setLoading(false);
      }
    }

    void init();
  }, [applyServerRole]);

  // A token refresh (incl. the silent ones lib/api.ts triggers on 401/403) may change identity
  // fields; it must NOT change the role — keep the server role and re-verify it. A null user here
  // means the new token could not be decoded — keep the current state rather than logging out.
  useEffect(() => {
    return authLib.onSessionRefreshed((next) => {
      if (!next) return;
      const prev = userRef.current;
      if (!prev) return; // init/login apply the server role themselves
      const merged: CurrentUser = {
        ...next,
        role: prev.userId === next.userId ? prev.role : "engineer",
      };
      setUser((p) => (sameUser(p, merged) ? p : merged));
      void recheckRole();
    });
  }, [recheckRole]);

  // API-layer signals: a role-denied 403 → re-check; ACCOUNT_DEACTIVATED → end the session.
  useEffect(() => {
    return onAuthEvent((event) => {
      if (event === "account-deactivated") {
        if (userRef.current) endSession("deactivated");
        return;
      }
      void recheckRole();
    });
  }, [endSession, recheckRole]);

  // Periodic + on-focus re-check while signed in.
  const signedInUserId = user?.userId;
  useEffect(() => {
    if (!signedInUserId) return undefined;
    const onFocus = (): void => {
      void recheckRole();
    };
    const onVisibility = (): void => {
      if (document.visibilityState === "visible") void recheckRole();
    };
    const interval = window.setInterval(() => {
      void recheckRole();
    }, ROLE_RECHECK_INTERVAL_MS);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [signedInUserId, recheckRole]);

  const refreshUser = useCallback(async (): Promise<boolean> => {
    const refreshed = await authLib.refreshTokens();
    if (refreshed) await recheckRole();
    return refreshed;
  }, [recheckRole]);

  const login = useCallback(
    async (email: string, password: string) => {
      setLoading(true);
      try {
        sessionEpochRef.current += 1;
        setRoleVerified(false);
        const identity = await authLib.login(email, password);
        const status = await applyServerRole(identity);
        if (status === "deactivated") {
          throw new Error(DEACTIVATED_MESSAGE);
        }
      } finally {
        setLoading(false);
      }
    },
    [applyServerRole],
  );

  const logout = useCallback(() => {
    endSession();
  }, [endSession]);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    loading,
    roleVerified,
    login,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
